import React, { useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import Autosuggest from 'react-autosuggest';
import moment from 'moment';
import { Row, Cell, Label, Box, FlexRow, FlexCol, Stack } from 'sarsaparilla';
import { get, flatMap, keys, escapeRegExp } from 'lodash';

import { MONTH_DAY_YEAR_FORMAT } from 'ui-permit/src/constants';

import { useValidation } from '../hooks/useValidation';
import AutoSuggestionContainer from './AutoSuggestionContainer';

const propTypes = {
    selectedEntryDate: PropTypes.shape({}).isRequired,
    selectedExitDate: PropTypes.shape({}),
    selectedCampsites: PropTypes.shape({}),
    divisions: PropTypes.objectOf(PropTypes.shape({})).isRequired,
    divisionCampsites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    permitCampsites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    stayNightCampsiteSelected: PropTypes.func.isRequired,
    suggestions: PropTypes.shape({}),
    renderSuggestion: PropTypes.func,
    onFormValidate: PropTypes.func.isRequired,
    campsiteLabel: PropTypes.string,
    inputRefs: PropTypes.arrayOf(PropTypes.func),
    validationHighlight: PropTypes.string,
    validationHighlightInput: PropTypes.string,
};

// Use your imagination to render suggestions.
const renderSuggestion = (suggestion) => {
    return (
        <Row>
            <Cell width={8}>{suggestion.name}</Cell>
            <Cell width={2}>{get(suggestion, 'area') ?? get(suggestion.division, 'district')}</Cell>
            <Cell width={2}>{get(suggestion, 'code') ?? get(suggestion.division, 'code')}</Cell>
        </Row>
    );
};

const transformCampsitePref = (selectedCampsites) => {
    const accum = {};
    keys(selectedCampsites).forEach((k) => {
        accum[moment.utc(k).toJSON()] = selectedCampsites[k];
    });
    return accum;
};

export default function CampsiteSelectionList({
    selectedCampsites,
    divisions,
    divisionCampsites,
    permitCampsites,
    selectedExitDate,
    selectedEntryDate,
    campsiteLabel,
    renderSuggestion: renderSuggestionFromProps,
    stayNightCampsiteSelected,
    inputRefs,
    validationHighlight,
    validationHighlightInput,
}) {
    // Autosuggest is a controlled component.
    // This means that you need to provide an input value
    // and an onChange handler that updates this value (see below).
    // Suggestions also need to be provided to the Autosuggest,
    // and they are initially empty because the Autosuggest is closed.

    const [value, setValue] = useState({});
    const [suggestions, setSuggestions] = useState({});
    const [divisionCampsitesState, setDivisionCampsites] = useState([]);
    const [permitCampsitesState, setPermitCampsites] = useState([]);

    let isClickedOnAutosuggest = false;
    let noFilterSuggestions = true;

    const refreshPermitSites = useCallback(() => {
        const siteIteratee = (site) => {
            const siteObj = {
                ...site,
                division: {},
            };
            if (site.division_ids.length > 0) {
                siteObj.division = get(divisions, site.division_ids[0], {});
            }
            return siteObj;
        };
        const flatDivisionCampsites = flatMap(divisionCampsites, siteIteratee);
        const flatPermitCampsites = flatMap(permitCampsites, siteIteratee);

        setDivisionCampsites(flatDivisionCampsites);
        setPermitCampsites(flatPermitCampsites);
    }, [divisionCampsites, divisions, permitCampsites]);

    const checkIsValidDate = () => {
        return moment.utc(selectedEntryDate).isBefore(moment.utc(selectedExitDate));
    };

    useEffect(() => {
        refreshPermitSites();
    }, [divisions, divisionCampsites, permitCampsites, refreshPermitSites]);

    useEffect(() => {
        setValue(transformCampsitePref(selectedCampsites));
    }, [selectedCampsites]);

    const { isInvalid } = useValidation(
        { value: checkIsValidDate(), id: 'autosuggest-input' },
        `${campsiteLabel} is required.`,
        inputRefs[0]
    );

    const firtInputDate = moment.utc(selectedEntryDate).toJSON();

    const { isInvalid: isInputInvalid } = useValidation(
        {
            value: !checkIsValidDate() || get(value, firtInputDate, ''),
            id: `autosuggest-input-${firtInputDate}`,
        },
        `${campsiteLabel} is required.`,
        inputRefs[1]
    );

    const onChange = (date) => (event, input) => {
        setValue({ ...value, [date]: input.newValue });

        if (input.newValue === '') {
            stayNightCampsiteSelected(date, '');
        }
    };

    // Autosuggest will call this function every time you need to update suggestions.
    // You already implemented this logic above, so just use it.
    const onSuggestionsFetchRequested = (date, index) => ({ value: suggestedValue }) => {
        let ret = [];

        if (isClickedOnAutosuggest) {
            isClickedOnAutosuggest = false;
        } else if (index === 0) {
            ret = [...divisionCampsitesState];
        } else {
            ret = [...permitCampsitesState];
        }

        const textValue = noFilterSuggestions ? '' : escapeRegExp(suggestedValue);
        const matcher = new RegExp(textValue, 'i');
        ret = ret.filter((item) => {
            return matcher.test(
                `${item.division.code} ${item.division.district} ${item.name} ${item.area} ${item.code}`
            );
        });
        const newSuggestions = { ...suggestions, [date]: ret };
        setSuggestions(newSuggestions);
    };

    // Autosuggest will call this function every time you need to clear suggestions.
    const onSuggestionsClearRequested = (date) => () => {
        const newSuggestions = { ...suggestions, [date]: [] };
        setSuggestions(newSuggestions);
    };

    // When suggestion is clicked, Autosuggest needs to populate the input
    // based on the clicked suggestion. Teach Autosuggest how to calculate the
    // input value for every given suggestion.
    const getSuggestionValue = (date) => (suggestion) => {
        stayNightCampsiteSelected(date, suggestion.name);
        isClickedOnAutosuggest = true;
        return suggestion.name;
    };

    const keyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const renderSuggestionsContainer = (props) => {
        return <AutoSuggestionContainer {...props} entranceLabel={campsiteLabel} />;
    };

    const onAutosuggestClick = () => {
        noFilterSuggestions = true;
    };

    const shouldRenderSuggestions = (SuggestionValue) => {
        noFilterSuggestions = true;
        const selectedValues = Object.values(selectedCampsites);
        if (!selectedValues?.includes?.(SuggestionValue)) {
            noFilterSuggestions = false;
        }
        return true;
    };

    const rows = [];
    for (
        let counter = moment.utc(selectedEntryDate);
        counter.isBefore(moment.utc(selectedExitDate));
        counter.add(1, 'day')
    ) {
        rows.push({ date: counter.toJSON() });
    }

    const itineraryList = rows.map((row, index) => {
        // Autosuggest will pass through all these props to the input.
        const kkey = row.date;
        const isFirstRow = index === 0;
        const inputProps = {
            id: `autosuggest-input-${kkey}`,
            placeholder: 'Type to select from the list...',
            className: isFirstRow && (isInputInvalid || validationHighlightInput) ? 'rec-error' : '',
            'aria-label': `autosuggest-input-${kkey}`,
            value: get(value, kkey, ''),
            onChange: onChange(kkey),
            onKeyPress: keyPress,
            onClick: onAutosuggestClick,
        };

        return (
            <FlexRow key={`row-${kkey}`}>
                <FlexCol xs={12} sm={4}>
                    <Label isRequired={isFirstRow} htmlFor={`auto-sugest-${kkey}`}>
                        {moment.utc(kkey).format(MONTH_DAY_YEAR_FORMAT)}
                    </Label>
                </FlexCol>
                <FlexCol xs={12} sm={8}>
                    <Autosuggest
                        id={`auto-sugest-${kkey}`}
                        alwaysRenderSuggestions
                        shouldRenderSuggestions={shouldRenderSuggestions}
                        value={selectedCampsites[kkey]}
                        suggestions={get(suggestions, kkey, [])}
                        onSuggestionsFetchRequested={onSuggestionsFetchRequested(
                            kkey,
                            index
                        )}
                        onSuggestionsClearRequested={onSuggestionsClearRequested(kkey)}
                        getSuggestionValue={getSuggestionValue(kkey)}
                        renderSuggestion={renderSuggestionFromProps || renderSuggestion}
                        renderSuggestionsContainer={renderSuggestionsContainer}
                        inputProps={inputProps}
                    />
                </FlexCol>
            </FlexRow>
        );
    });

    return (
        <Box
            padding="md"
            border="gray"
            background="gray-2"
            className={isInvalid || validationHighlight ? 'permit-validation-highlight' : ''}
        >
            <Stack space={['xxs', 'xs']}>
                <FlexRow>
                    <FlexCol xs={12} sm={4}>
                        <b>Date</b>
                    </FlexCol>
                    <FlexCol xs={12} sm={8}>
                        <Label
                            id="autosuggest-input"
                            htmlFor={`autosuggest-input-${firtInputDate}`}
                        >
                            {campsiteLabel}
                        </Label>
                    </FlexCol>
                </FlexRow>
                {itineraryList}
            </Stack>
        </Box>
    );
}

CampsiteSelectionList.propTypes = propTypes;

CampsiteSelectionList.defaultProps = {
    suggestions: {},
    campsiteLabel: 'Campsite Locations',
};
